import { Component } from "projen";
import { GitHub, GithubWorkflow } from "projen/lib/github";
import { JobPermission } from "projen/lib/github/workflows-model";
import { renderHomeRepositoryCondition } from "./home-repository";
import { AwsDeploymentTarget } from "../aws/aws-deployment-target";
import { MonorepoProject } from "../projects/monorepo-project";

/**
 * Default branch-delete patterns that trigger the teardown workflow.
 */
export const DEFAULT_TEARDOWN_BRANCH_PATTERNS: ReadonlyArray<string> = [
  "feat/*",
  "fix/*",
  "feature/*",
];

/**
 * Options for {@link AwsTeardownWorkflow}.
 */
export interface AwsTeardownWorkflowOptions {
  /**
   * Targets to scan for orphaned CloudFormation stacks.
   */
  readonly awsDestructionTargets: Array<AwsDeploymentTarget>;

  /**
   * Name of the tag containing the repo name. Used to find CloudFormation
   * stacks associated with this repo.
   */
  readonly repoTagName: string;

  /**
   * Name of the tag containing the stage type (e.g. dev, stage, prod).
   */
  readonly stageTypeTagName: string;

  /**
   * Name of the tag containing the environment type (e.g. primary, secondary).
   */
  readonly environmentTypeTagName: string;

  /**
   * Name of the tag where branch names are stored. Used to determine if a
   * stack is orphaned (no matching branch).
   */
  readonly branchNameTagName: string;

  /**
   * Branch patterns whose deletion triggers the teardown workflow.
   *
   * @default ["feat/*", "fix/*", "feature/*"]
   */
  readonly deleteBranchPatterns?: Array<string>;

  /**
   * Pin the teardown jobs to one repository, as an `owner/repo` slug.
   *
   * Guards every teardown job with `github.repository == '<owner>/<repo>'` so a
   * mirror or clone of the consumer repo does not delete stacks it does not
   * own. Especially load-bearing here: a clone's branch list differs from the
   * home repo's, so an unguarded scheduled run would classify live stacks as
   * orphans.
   *
   * @see `docs/packages/@codedrifters/configulator/workflows/aws-deploy-workflow.md`
   * @default - no repository guard is emitted
   */
  readonly homeRepository?: string;
}

/**
 * Resolve the set of branch-delete patterns to wire into the workflow's
 * `on.delete.branches` trigger.
 *
 * Precedence:
 * 1. Explicit `deleteBranchPatterns` from options.
 * 2. Wildcard patterns (containing `*`) derived from the destruction
 *    targets' configured branches, deduped.
 * 3. {@link DEFAULT_TEARDOWN_BRANCH_PATTERNS} as a last-resort fallback.
 */
const resolveBranchPatterns = (
  explicit: Array<string> | undefined,
  targets: ReadonlyArray<AwsDeploymentTarget>,
): Array<string> => {
  if (explicit && explicit.length > 0) {
    return [...explicit];
  }

  const derived = Array.from(
    new Set(
      targets
        .flatMap((target) => target.branches.map((b) => b.branch))
        .filter((branch): branch is string => typeof branch === "string")
        .filter((branch) => branch.includes("*")),
    ),
  );

  if (derived.length > 0) {
    return derived;
  }

  return [...DEFAULT_TEARDOWN_BRANCH_PATTERNS];
};

/**
 * Scheduled GitHub Actions workflow that tears down orphaned CloudFormation
 * stacks whose associated branch no longer exists.
 */
export class AwsTeardownWorkflow extends Component {
  constructor(
    public rootProject: MonorepoProject,
    options: AwsTeardownWorkflowOptions,
  ) {
    super(rootProject);

    const {
      awsDestructionTargets,
      repoTagName,
      stageTypeTagName,
      environmentTypeTagName,
      branchNameTagName,
      deleteBranchPatterns,
      homeRepository,
    } = options;

    if (!repoTagName) {
      throw new Error("AwsTeardownWorkflow requires `repoTagName`");
    }
    if (!stageTypeTagName) {
      throw new Error("AwsTeardownWorkflow requires `stageTypeTagName`");
    }
    if (!environmentTypeTagName) {
      throw new Error("AwsTeardownWorkflow requires `environmentTypeTagName`");
    }
    if (!branchNameTagName) {
      throw new Error("AwsTeardownWorkflow requires `branchNameTagName`");
    }

    if (!(rootProject instanceof MonorepoProject)) {
      throw new Error(
        "AwsTeardownWorkflow requires the root project to be a MonorepoProject",
      );
    }

    const github = GitHub.of(this.rootProject);

    if (!github) {
      throw new Error(
        "AwsTeardownWorkflow requires a GitHub component in the root project",
      );
    }

    const branchPatterns = resolveBranchPatterns(
      deleteBranchPatterns,
      awsDestructionTargets,
    );

    /**
     * Optional repository pin. Unset (the default) emits no `if:` at all, so
     * the generated workflow is unchanged.
     */
    const homeRepositoryCondition = renderHomeRepositoryCondition(
      homeRepository,
      "AwsTeardownWorkflow",
    );

    const workflow = new GithubWorkflow(github, "teardown-dev");
    workflow.on({
      workflowDispatch: {},
      schedule: [
        {
          cron: "32 6 * * *",
        },
      ],
      delete: {
        branches: branchPatterns,
      },
    });

    awsDestructionTargets.forEach((target) => {
      const {
        awsStageType,
        deploymentTargetRole,
        account,
        region,
        ciDeploymentConfig,
      } = target;
      const { roleArn } = ciDeploymentConfig ?? {};

      workflow.addJob(
        `teardown-${awsStageType}-${deploymentTargetRole}-${account}-${region}`.toLowerCase(),
        {
          name: `Teardown Stacks in ${awsStageType}/${deploymentTargetRole}/${account}/${region}`,
          runsOn: ["ubuntu-latest"],
          ...(homeRepositoryCondition
            ? { if: `\${{ ${homeRepositoryCondition} }}` }
            : {}),
          permissions: {
            contents: JobPermission.READ,
            idToken: JobPermission.WRITE,
          },
          env: {
            REPO: "${{ github.repository }}",
            REGIONS: [region].join(" "),
          },
          steps: [
            {
              name: `AWS Creds ${account}/${region}`,
              uses: "aws-actions/configure-aws-credentials@v6",
              with: {
                "role-to-assume": roleArn,
                "aws-region": region,
                "role-duration-seconds": 900,
              },
            },
            {
              name: "Fetch All Branches",
              id: "fetch_branches",
              uses: "actions/github-script@v9",
              with: {
                script: [
                  "const all = await github.paginate(github.rest.repos.listBranches, {",
                  "  owner: context.repo.owner,",
                  "  repo: context.repo.repo,",
                  "  per_page: 100",
                  "});",
                  "const names = all.map(b => b.name);",
                  "console.log(`Found branches: ${names}`);",
                  'core.setOutput("json", JSON.stringify(names));',
                ].join("\n"),
              },
            },
            {
              name: "Save Branches to File",
              run: [
                'echo "Saving branches to file"',
                "echo '${{ steps.fetch_branches.outputs.json }}' | jq -r '.[]' | sort -u > branches.txt",
                'echo "Branches:"',
                "cat branches.txt",
              ].join("\n"),
            },
            {
              name: "Find Stacks by Tag",
              id: "find_stacks",
              run: [
                "set -euo pipefail",
                ": > candidates.txt  # columns: arn region branchTag",
                "# Build tag filters",
                `TAG_FILTERS=( "Key=${repoTagName},Values=$REPO" )`,
                `TAG_FILTERS+=( "Key=${stageTypeTagName},Values=${awsStageType}" )`,
                `TAG_FILTERS+=( "Key=${environmentTypeTagName},Values=${deploymentTargetRole}" )`,
                "for r in $REGIONS; do",
                `  echo "Scanning region: $r"`,
                "  aws resourcegroupstaggingapi get-resources \\",
                '    --region "$r" \\',
                '    --resource-type-filters "cloudformation:stack" \\',
                '    --tag-filters "${TAG_FILTERS[@]}" \\',
                ` | jq -r --arg r "$r" '`,
                "    .ResourceTagMappingList[]",
                "    | . as $res",
                `    | ($res.Tags[] | select(.Key=="${branchNameTagName}") | .Value) as $branch`,
                '    | [$res.ResourceARN, $r, ($branch // "")]',
                "    | @tsv",
                "    ' >> candidates.txt",
                "done",
                "echo 'Tagged stacks:'",
                `(echo -e "ARN\\tREGION\\tBRANCH"; cat candidates.txt) | column -t -s $'\\t'`,
              ].join("\n"),
            },
            {
              name: "Determine Orphan Stacks (No Matching Branch)",
              run: [
                "set -euo pipefail",
                ": > orphans.txt  # arn region branch",
                "while IFS=$'\\t' read -r arn region branch; do",
                '  [ -z "$arn" ] && continue',
                '  if [ -z "$branch" ]; then',
                "    # If no Branch tag, treat as not-a-preview; skip",
                "    continue",
                "  fi",
                '  if ! grep -Fxq "$branch" branches.txt; then',
                '    echo -e "$arn\\t$region\\t$branch" >> orphans.txt',
                "  fi",
                "done < candidates.txt",
                "",
                "if [ -s orphans.txt ]; then",
                '  echo "Orphan stacks (no matching branch):"',
                "  (echo -e \"ARN\\tREGION\\tBRANCH\"; cat orphans.txt) | column -t -s $'\\t'",
                "else",
                '  echo "No orphan stacks found."',
                "fi",
              ].join("\n"),
            },
            {
              name: "Delete Orphan Stacks",
              if: "hashFiles('orphans.txt') != ''",
              run: [
                "set -euo pipefail",
                "while IFS=$'\\t' read -r arn region branch; do",
                '  [ -z "$arn" ] && continue',
                "  stack_name=$(cut -d'/' -f2 <<<\"$arn\")",
                '  echo "Deleting $stack_name (branch=$branch) in $region"',
                '  aws cloudformation delete-stack --region "$region" --stack-name "$stack_name" || true',
                "done < orphans.txt",
              ].join("\n"),
            },
          ],
        },
      );
    });
  }
}
